import { ReactNode, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { useScrollEdgeFade } from '@/hooks/scroll/use-scroll-edge-fade';
import { cn } from '@/lib/utils';

import { getTimeOffTypeIcon } from '../../constants/time-off-type-icon-map';
import { TimeOffBalance } from '../../types/time-off-balance';

interface TimeOffBalancesCompactProps {
  balances: TimeOffBalance[];
  title?: ReactNode;
}

const renderTooltip = (
  balance: TimeOffBalance,
  t: ReturnType<typeof useTranslation>['t']
): React.ReactNode => {
  const formatDays = (value: number) => t('balances.daysValue', { count: value });

  return (
    <div className="flex min-w-[180px] flex-col gap-1">
      <p className="text-xs font-semibold">{balance.type.displayName}</p>
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="text-muted-foreground">{t('balances.rows.balance')}</span>
        <span className="font-medium">{formatDays(balance.balanceDays)}</span>
      </div>
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="text-muted-foreground">{t('balances.rows.available')}</span>
        <span className="font-medium">
          {balance.requestedDays != null
            ? `${formatDays(balance.availableDays)} / ${formatDays(balance.requestedDays)}`
            : formatDays(balance.availableDays)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="text-muted-foreground">{t('balances.rows.totalBooked')}</span>
        <span className="font-medium">{formatDays(balance.bookedDays)}</span>
      </div>
    </div>
  );
};

export const TimeOffBalancesCompact = ({ balances, title }: TimeOffBalancesCompactProps) => {
  const { t } = useTranslation('authenticated.timeOff');
  const scrollRef = useRef<HTMLDivElement>(null);
  const { showLeftFade, showRightFade } = useScrollEdgeFade(scrollRef);

  return (
    <div className="relative flex h-full items-center bg-background">
      <span className="mr-3 shrink-0 text-lg font-semibold">
        {title ?? t('balances.compact.title')}
      </span>
      <div
        ref={scrollRef}
        className={cn(
          'flex flex-1 gap-2 overflow-x-auto',
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
        )}
      >
        {balances.map(balance => {
          const Icon = getTimeOffTypeIcon(balance.type.code);
          return (
            <SimpleTooltip key={balance.type.id} content={renderTooltip(balance, t)}>
              <button
                type="button"
                className={cn(
                  'flex shrink-0 items-center gap-2 rounded-full border border-border bg-muted px-3 py-1.5',
                  'text-xs font-medium text-foreground transition-colors hover:bg-muted/70',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                )}
                aria-label={t('balances.compact.ariaLabel', {
                  type: balance.type.displayName,
                  count: balance.availableDays,
                })}
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-foreground text-background">
                  <Icon className="h-3 w-3" />
                </span>
                <span className="max-w-[120px] truncate text-muted-foreground">
                  {balance.type.displayName}
                </span>
                <span className="font-semibold tabular-nums">
                  {t('balances.daysValue', { count: balance.availableDays })}
                </span>
              </button>
            </SimpleTooltip>
          );
        })}
      </div>

      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-background to-transparent transition-opacity duration-200',
          showLeftFade ? 'opacity-100' : 'opacity-0'
        )}
      />
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent transition-opacity duration-200',
          showRightFade ? 'opacity-100' : 'opacity-0'
        )}
      />
    </div>
  );
};
